
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CreateAdSuggestionsParams {
  clinicaId: string;
  startDate: string;
  endDate: string;
  deliveryMethod: 'system' | 'whatsapp';
  phoneNumber?: string;
}

/**
 * Hook para criar sugestões de anúncios
 * 
 * Este hook cria um registro na tabela ad_suggestions e chama a Edge Function
 * que processa a análise das conversas via webhook n8n para gerar sugestões
 * de anúncios personalizadas com base nas dores e desejos dos leads.
 */
export const useCreateAdSuggestions = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: CreateAdSuggestionsParams) => {
      // Primeiro, criar o registro na tabela ad_suggestions
      const { data: suggestion, error: insertError } = await supabase
        .from('ad_suggestions')
        .insert({
          clinica_id: params.clinicaId,
          start_date: params.startDate,
          end_date: params.endDate,
          delivery_method: params.deliveryMethod,
          phone_number: params.phoneNumber,
          status: 'pending'
        })
        .select()
        .single();

      if (insertError) {
        throw new Error(`Erro ao criar sugestão: ${insertError.message}`);
      }

      // Chamar a Edge Function para processar as sugestões
      const { data, error } = await supabase.functions.invoke('generate-ad-suggestions', {
        body: {
          suggestionId: suggestion.id,
          clinicaId: params.clinicaId,
          startDate: params.startDate,
          endDate: params.endDate,
          deliveryMethod: params.deliveryMethod,
          phoneNumber: params.phoneNumber
        }
      });

      if (error) {
        throw new Error(`Erro ao processar sugestões: ${error.message}`);
      }

      return { suggestion, result: data };
    },
    onSuccess: () => {
      toast({
        title: "Sugestões de Anúncios Iniciadas",
        description: "O processamento foi iniciado. Você será notificado quando as sugestões estiverem prontas.",
      });
    },
    onError: (error) => {
      console.error('Erro ao criar sugestões de anúncios:', error);
      toast({
        title: "Erro",
        description: `Não foi possível gerar as sugestões: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};
